"use client";

import { adminCreateTag, AdminCreateTagData, adminDeleteTag, AdminDeleteTagData, adminGetTag, AdminGetTagData, adminUpdateTag, AdminUpdateTagData, updateTagSpeakerProfile } from "@/api";
import { appState } from "@/app/appState";
import { tag } from "@/app/typing/tags";
import { usePathParam, useLocalNumberList } from "@/components/path-params";
import { ActionHandler, DefEdit, EntryItem, RichTextType, StateColumnMultipleType, StateColumnSingleType, StateColumnSwitchType, TextInputType, useRefState } from "@/components/UI/tools";
import { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Request } from "@/components/CRUD";
import { PageBox } from "@/components/UI/page";
import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { dispersedMatch } from "@/components/searchUtils";
import { runInAction, set } from "mobx";
import { Calendar } from "@/components/UI/svgs";

import { useAttributesUnfurler } from "@/app/workspace/chat/chatObjectEditorWindow/objectEditors/useAttributesUnfurler";
import { appendProfile } from "@/utils/speakerProfileSerializer";
import { SpeakerProfileRecorder } from "@/components/SpeakerProfileRecorder";

const TagEntry = observer(({ id, setId, isModal, initialValues }: { id?: number; setId: (id?: number) => void; isModal?: boolean; initialValues?: Partial<tag> }) => {
    const [tag, setTag] = useState<tag>();
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [refresh, setRefresh] = useState(0);

    const [lock, setLock] = useRefState(false)
    const [edit, setEdit] = useState(isModal)


    const [ids, setIds] = useLocalNumberList("tag_ids");

    const [name, setName] = useRefState<string>("");
    const [description, setDescription] = useRefState<string | undefined>(undefined);
    const [permissionType, setPermissionType] = useRefState<number | undefined>(undefined);
    const [tprOrganisationIds, setTprOrganisationIds] = useRefState<number[]>([]);
    const [organisationSearch, setOrganisationSearch] = useState<string>("")
    const [attributes, setAttributes] = useRefState<Record<string, any> | undefined>(undefined);
    const [scope, setScope] = useRefState<"me" | "organisation" | undefined>(undefined);

    if (id === -1 && initialValues) {
        if (initialValues.scope) setScope(initialValues.scope);
        if (initialValues.permission_type) setPermissionType(initialValues.permission_type);
        if (initialValues.attributes) setAttributes(initialValues.attributes);
    }


    const updateAttribute = (key: string, val: any) => {
        setAttributes({ ...(attributes.current || {}), [key]: val });
    };

    const attributesUnfurled = useAttributesUnfurler(attributes.current, updateAttribute);

    const createTag = Request<AdminCreateTagData, any>(
        adminCreateTag,
        () => ({
            body: {
                name: name.current,
                description: description.current ?? "",
                scope: scope.current as "me" | "organisation",
                permission_type: permissionType.current!,
                tpr_organisation_ids: tprOrganisationIds.current,
                attributes: attributes.current,
            },
        }),
        setLoading,
        (data: any) => {
            setId(data?.tag_id)
            appState.getTags({ pageNumber: 0, search: "" });
        },
        (error) => {
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorCreateTag"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => name.current.length > 0 && scope.current != undefined && permissionType.current != undefined,
    )

    const deleteTag = Request<AdminDeleteTagData, any>(
        adminDeleteTag,
        () => ({
            body: {
                id: id!
            },
        }),
        setDeleting,
        (data: any) => {
            setId(undefined);
            appState.getTags({ pageNumber: 0, search: "" });

        },
        (error) => {
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorDeleteTag") });
        },
        () => id != undefined && id > 0,
    )


    const updateTag = Request<AdminUpdateTagData, any>(
        adminUpdateTag,
        () => ({
            body: {
                id: id!,
                name: name.current,
                description: description.current ?? "",
                scope: scope.current as "me" | "organisation",
                permission_type: permissionType.current!,
                tpr_organisation_ids: tprOrganisationIds.current,
                attributes: attributes.current,
            },
        }),
        setLoading,
        (data) => {
            appState.getTags({ pageNumber: 0, search: "" });
        },

        (error) => {
            setRefresh(p => p + 1)
            setEdit(false)
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateTag"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => id != undefined && id > 0 && name.current.length > 0 && scope.current != undefined && permissionType.current != undefined,
    )

    const getTag = Request<AdminGetTagData, any>(
        adminGetTag,
        () => ({
            body: {
                id: id!,
                get_extensions: true
            },
        }),
        setLoading,
        (data: any) => {
            setTag(data.tag);
        },
        (error) => {
            setTag(undefined)
            appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorGetTag") });
        },
        () => id != undefined && id > 0,
    )

    useEffect(() => {

        setTag(undefined);
        if (id && id > 0)
            getTag();
    }, [id]);

    const refreshRefs = () => {
        if (tag) {
            // Update input field values directly through refs
            setLock(tag.user_lock ? !(tag.user_lock == appState.user?.id) : false)

            setName(tag.name);
            setDescription(tag.description);
            setScope(tag.scope)
            setPermissionType(tag.permission_type)
            setTprOrganisationIds(tag.tpr_organisation_ids ?? [])
            setAttributes(tag.attributes ?? undefined)

        } else {
            // Clear form when no artifact is selected
            setName("");
            setPermissionType(undefined)
            setTprOrganisationIds([])
            setScope(undefined)
            setAttributes(undefined)
        }
    };

    // Update form values when artifact changes

    useEffect(() => {

        refreshRefs();
        setRefresh((prev) => prev + 1);
        console.log("TAAAG", tag)
    }, [tag]);


    return (
        <PageBox isModal={isModal}>

            <div className="h-full w-full ">
                {
                    (tag && tag.id == id) || id == -1 ?

                        <EntryItem
                            lock={lock.current}
                            setEdit={(val: boolean) => DefEdit(val, id, setEdit, "tag")}
                            edit={edit}
                            isModal={isModal}
                            name={appState.t("objects.list.tag")}
                            idParams={{ singular: { get: id, set: setId }, plural: { get: ids, set: setIds } }}
                            loading={loading}
                            deleting={deleting}
                            readonly={(![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) || ![4, 5, 6, 7].includes(tag?.effective_permission_type ?? 0)) && id != -1}
                            refreshCount={refresh}
                            createEntry={createTag}
                            deleteEntry={deleteTag}
                            updateEntry={updateTag}
                            refColumns={[
                                {
                                    type: attributes.current?.is_client ? "textarea" : "richtext",
                                    label: appState.t("objects.description"),
                                    value: description.current,
                                    onChange: setDescription,
                                    validator: (val: string | undefined) => (val ? true : false),
                                },
                                ...attributesUnfurled.refColumns
                            ]}
                            nameColumn={{
                                type: "text",
                                value: name.current,
                                onChange: (x) => {
                                    setName(x);
                                },
                                validator: (val: string) => /^[\p{L}0-9_\s-]+$/u.test(val),
                                label: appState.t("objects.variableName"),
                            }}
                            stateColumns={[{
                                label: appState.t("objects.scope"),
                                type: "switch",
                                hidden: initialValues?.scope !== undefined,
                                onChange: (val) => {
                                    const next = val === appState.t("objects.scope.me") || val === "me" ? "me"
                                        : val === appState.t("objects.scope.organisation") || val === "organisation" ? "organisation"
                                        : undefined;
                                    setScope(next)
                                    setRefresh(p => p + 1)
                                },
                                value: scope.current === "me" ? appState.t("objects.scope.me")
                                    : scope.current === "organisation" ? appState.t("objects.scope.organisation")
                                    : scope.current,
                                items: [{ name: appState.t("objects.scope.me") }, { name: appState.t("objects.scope.organisation") }],
                                validator: (val?: string) => val ? true : false,
                            },
                            {
                                label: appState.t("objects.access"),
                                hidden: initialValues?.permission_type !== undefined,
                                type: "single",
                                onChange: setPermissionType,
                                value: permissionType.current,
                                items: [{ id: 7, name: appState.t("objects.access.full") }, { id: 3, name: appState.t("objects.access.read") }, { id: 1, name: appState.t("objects.access.use") }],
                                validator: (val: number | undefined) => val ? true : false,
                            },
                            scope.current == "organisation" ?
                                {
                                    label: appState.t("objects.shareTo"),

                                    type: "multiple",
                                    onChange: setTprOrganisationIds,
                                    search: { get: organisationSearch, set: setOrganisationSearch },
                                    value: tprOrganisationIds.current,
                                    items: appState.organisations?.filter(x => {
                                        if (x.id == appState.user?.active_organisation.id) return false;
                                        return dispersedMatch(organisationSearch, x.name);
                                    }).map((x) => {
                                        return { id: x.id, name: x.name, defaultValue: tprOrganisationIds.current?.includes(x.id) };
                                    }),
                                    validator: (val: number[]) => true,
                                }
                                :
                                undefined,
                            ...attributesUnfurled.stateColumns.filter(x => x.label != "is_client")
                            ]}
                        />
                        :
                        null
                }

                {/* Calendar indicator & voice profile */}
                {!initialValues?.attributes?.is_client && !isModal && id && id > 0 && [4, 5, 6, 7].includes(tag?.effective_permission_type ?? 0) && (
                    <div className="flex flex-row justify-end gap-0">
                        {/*Calendar indicator*/}
                        <ActionHandler
                            title={appState.t("objects.calendarActive")}
                            hidden={!tag?.has_calendar}
                            funq={(e) => { }}
                            disabled={true}
                            glow={tag?.has_calendar}
                        >
                            <Calendar />
                        </ActionHandler>
                        <ActionHandler
                            title={appState.t("objects.voiceProfile")}
                            keysTrigger=""
                            funq={async () => {

                                let deviceLabel = "current microphone";
                                try {
                                    const temporaryStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                                    deviceLabel = temporaryStream.getAudioTracks()[0]?.label || "current microphone";
                                    temporaryStream.getTracks().forEach(t => t.stop());
                                } catch (err) {
                                    console.warn("Could not get microphone for label", err);
                                }

                                appState.appendModal({
                                    title: appState.t("objects.voiceProfile"),
                                    body: (
                                        tag?.attributes?.speaker_profile && tag?.attributes?.speaker_profile.length > 0
                                            ? (
                                                <div className="flex flex-col">
                                                    <div className="flex flex-col w-full mb-1">
                                                        <p>Tag already has a voice profile.</p>
                                                        <p>You can remove it with the button below.</p>
                                                    </div>
                                                    <div className="flex flex-row w-full justify-start">
                                                        <ActionHandler
                                                            funq={() => {
                                                                updateTagSpeakerProfile({ body: { id: id!, speaker_profile: [] } })
                                                                    .then(() => {
                                                                        runInAction(() => {
                                                                            appState.getTags({ pageNumber: 0, search: "" });
                                                                            getTag();
                                                                            appState.popModal();
                                                                        });
                                                                    });
                                                            }}
                                                        >{appState.t("objects.clearSpeakerProfile")}</ActionHandler>
                                                    </div>
                                                </div>)
                                            : (
                                                <div className="p-2">

                                                    <SpeakerProfileRecorder
                                                        existingProfile={tag?.attributes?.speaker_profile}
                                                        label={tag?.name ?? "tag"}
                                                        deviceLabel={deviceLabel}
                                                        onProfileCaptured={(profile) => {
                                                            const updatedProfile = appendProfile(tag?.attributes?.speaker_profile, deviceLabel, profile);
                                                            updateTagSpeakerProfile({ body: { id: id!, speaker_profile: updatedProfile } })
                                                                .then(() => {
                                                                    runInAction(() => {
                                                                        appState.getTags({ pageNumber: 0, search: "" });
                                                                        getTag();
                                                                        appState.popModal();
                                                                    });
                                                                });
                                                        }}
                                                    />
                                                </div>
                                            )

                                    )

                                });
                            }}
                            glow={tag?.attributes?.speaker_profile && tag?.attributes?.speaker_profile.length > 0}
                            hidden={!tag?.attributes?.is_client}
                            disabled={loading}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                                <line x1="12" y1="19" x2="12" y2="23" />
                                <line x1="8" y1="23" x2="16" y2="23" />
                            </svg>
                        </ActionHandler>
                    </div>
                )}
            </div>

        </PageBox>
    );
});


export default TagEntry;
